'use client';

import React, { useState } from 'react';
import {
  FileText,
  Briefcase,
  Home,
  Heart,
  GraduationCap,
  Coffee,
  ArrowRight,
  Clock,
  Star } from
'lucide-react';

import GuideModal, { GuideContent } from './GuideModal';
import { useLanguage } from '@/lib/i18n';


const categoryIds = [
{ id: 'all', icon: Star, color: 'text-gray-600', bg: 'bg-gray-100' },
{ id: 'immigration', icon: FileText, color: 'text-primary-700', bg: 'bg-primary-50' },
{ id: 'travail', icon: Briefcase, color: 'text-blue-600', bg: 'bg-blue-50' },
{ id: 'logement', icon: Home, color: 'text-green-600', bg: 'bg-green-50' },
{ id: 'sante', icon: Heart, color: 'text-red-600', bg: 'bg-red-50' },
{ id: 'etudes', icon: GraduationCap, color: 'text-purple-600', bg: 'bg-purple-50' },
{ id: 'quotidien', icon: Coffee, color: 'text-amber-600', bg: 'bg-amber-50' }];


const prompts = [
// Immigration
{
  id: 'prompt-imm-001',
  category: 'immigration',
  title: 'Renouvellement du titre de séjour',
  title_ar: 'تجديد تصريح الإقامة',
  description: 'Étapes, documents requis et délais pour renouveler votre titre de séjour en France.',
  description_ar: 'الخطوات والوثائق المطلوبة والمدد الزمنية لتجديد تصريح إقامتك في فرنسا.',
  tags: ['ANEF', 'Préfecture', '2–4 mois'],
  difficulty: 'Intermédiaire',
  isFree: true,
  isPopular: true,
  timeEstimate: '~15 min de lecture'
},
{
  id: 'prompt-imm-002',
  category: 'immigration',
  title: 'Validation du visa long séjour (VLS-TS)',
  title_ar: 'التحقق من تأشيرة الإقامة الطويلة (VLS-TS)',
  description: 'Comment valider votre visa long séjour sur le portail ANEF dans les 3 mois après l\'arrivée.',
  description_ar: 'كيفية التحقق من تأشيرة إقامتك الطويلة على بوابة ANEF خلال 3 أشهر من الوصول.',
  tags: ['ANEF', 'Obligatoire', '3 mois'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: true,
  timeEstimate: '~10 min de lecture'
},
{
  id: 'prompt-imm-003',
  category: 'immigration',
  title: 'Changement de statut de séjour',
  title_ar: 'تغيير وضع الإقامة',
  description: 'Passer d\'un titre étudiant à salarié, ou demander un changement de motif de séjour.',
  description_ar: 'الانتقال من تصريح طالب إلى موظف، أو طلب تغيير سبب الإقامة.',
  tags: ['Préfecture', 'ANEF', 'Dossier'],
  difficulty: 'Avancé',
  isFree: false,
  isPopular: false,
  timeEstimate: '~20 min de lecture'
},
{
  id: 'prompt-imm-004',
  category: 'immigration',
  title: 'Demande de carte de résident 10 ans',
  title_ar: 'طلب بطاقة إقامة لمدة 10 سنوات',
  description: 'Conditions et procédure pour obtenir la carte de résident valable 10 ans selon l\'accord franco-tunisien.',
  description_ar: 'الشروط والإجراءات للحصول على بطاقة إقامة صالحة لمدة 10 سنوات وفق الاتفاقية الفرنسية التونسية.',
  tags: ['Accord bilatéral', '10 ans', 'Stabilité'],
  difficulty: 'Avancé',
  isFree: false,
  isPopular: false,
  timeEstimate: '~18 min de lecture'
},
{
  id: 'prompt-imm-005',
  category: 'immigration',
  title: 'Regroupement familial',
  title_ar: 'لمّ شمل الأسرة',
  description: 'Faire venir votre famille en France : conditions de ressources, logement et procédure OFII.',
  description_ar: 'إحضار عائلتك إلى فرنسا: شروط الموارد والسكن وإجراءات OFII.',
  tags: ['OFII', 'Famille', 'Ressources'],
  difficulty: 'Avancé',
  isFree: false,
  isPopular: true,
  timeEstimate: '~25 min de lecture'
},
{
  id: 'prompt-imm-006',
  category: 'immigration',
  title: 'Demande de naturalisation française',
  title_ar: 'طلب الجنسية الفرنسية',
  description: 'Conditions, dossier complet et délais pour acquérir la nationalité française.',
  description_ar: 'الشروط والملف الكامل والمدد الزمنية للحصول على الجنسية الفرنسية.',
  tags: ['Naturalisation', '5–7 ans', 'CIR'],
  difficulty: 'Avancé',
  isFree: false,
  isPopular: false,
  timeEstimate: '~30 min de lecture'
},
// Travail
{
  id: 'prompt-trav-001',
  category: 'travail',
  title: 'Comprendre sa fiche de paie',
  title_ar: 'فهم كشف الراتب',
  description: 'Décryptage des cotisations, brut vs net, congés payés et indemnités sur votre bulletin de salaire.',
  description_ar: 'شرح الاشتراكات والراتب الإجمالي مقابل الصافي والإجازات المدفوعة والتعويضات في كشف راتبك.',
  tags: ['Salaire', 'Cotisations', 'Net/Brut'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: true,
  timeEstimate: '~12 min de lecture'
},
{
  id: 'prompt-trav-002',
  category: 'travail',
  title: 'S\'inscrire à France Travail (Pôle Emploi)',
  title_ar: 'التسجيل في France Travail (بول إمبلوا)',
  description: 'Procédure d\'inscription, droits aux allocations chômage et accompagnement pour les étrangers.',
  description_ar: 'إجراءات التسجيل والحقوق في تعويضات البطالة والمرافقة للأجانب.',
  tags: ['France Travail', 'ARE', 'Chômage'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: true,
  timeEstimate: '~10 min de lecture'
},
{
  id: 'prompt-trav-003',
  category: 'travail',
  title: 'Autorisation de travail pour étrangers',
  title_ar: 'تصريح العمل للأجانب',
  description: 'Quand et comment obtenir une autorisation de travail en tant que ressortissant tunisien.',
  description_ar: 'متى وكيف تحصل على تصريح عمل بوصفك مواطناً تونسياً.',
  tags: ['Autorisation', 'Employeur', 'DIRECCTE'],
  difficulty: 'Intermédiaire',
  isFree: false,
  isPopular: false,
  timeEstimate: '~15 min de lecture'
},
// Logement
{
  id: 'prompt-log-001',
  category: 'logement',
  title: 'Demander les APL à la CAF',
  title_ar: 'طلب مساعدات السكن APL من CAF',
  description: 'Calculer vos droits aux aides au logement et faire votre demande en ligne sur caf.fr.',
  description_ar: 'احتساب حقوقك في مساعدات السكن وتقديم طلبك عبر الإنترنت على caf.fr.',
  tags: ['CAF', 'APL', 'Simulation'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: true,
  timeEstimate: '~8 min de lecture'
},
{
  id: 'prompt-log-002',
  category: 'logement',
  title: 'Garantie Visale pour votre location',
  title_ar: 'ضمان Visale لإيجارك',
  description: 'Comment obtenir la garantie Visale d\'Action Logement pour votre premier appartement.',
  description_ar: 'كيفية الحصول على ضمان Visale من Action Logement لشقتك الأولى.',
  tags: ['Visale', 'Caution', 'Action Logement'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: false,
  timeEstimate: '~10 min de lecture'
},
{
  id: 'prompt-log-003',
  category: 'logement',
  title: 'Droits du locataire et état des lieux',
  title_ar: 'حقوق المستأجر وجرد الممتلكات',
  description: 'Vos droits lors de la signature d\'un bail, l\'état des lieux d\'entrée et de sortie.',
  description_ar: 'حقوقك عند توقيع عقد الإيجار وجرد الممتلكات عند الدخول والخروج.',
  tags: ['Bail', 'Locataire', 'Droits'],
  difficulty: 'Intermédiaire',
  isFree: false,
  isPopular: false,
  timeEstimate: '~14 min de lecture'
},
// Santé
{
  id: 'prompt-san-001',
  category: 'sante',
  title: 'Obtenir sa carte Vitale',
  title_ar: 'الحصول على بطاقة Vitale',
  description: 'Inscription à l\'Assurance Maladie et demande de carte Vitale pour les nouveaux arrivants.',
  description_ar: 'التسجيل في التأمين الصحي وطلب بطاقة Vitale للقادمين الجدد.',
  tags: ['CPAM', 'Carte Vitale', 'Ameli'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: true,
  timeEstimate: '~8 min de lecture'
},
{
  id: 'prompt-san-002',
  category: 'sante',
  title: 'Choisir son médecin traitant',
  title_ar: 'اختيار طبيبك المعالج',
  description: 'Déclarer un médecin traitant sur ameli.fr pour bénéficier du remboursement optimal.',
  description_ar: 'الإعلان عن طبيب معالج على ameli.fr للاستفادة من أفضل تعويض.',
  tags: ['Médecin', 'Remboursement', 'Ameli'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: false,
  timeEstimate: '~6 min de lecture'
},
// Études
{
  id: 'prompt-etu-001',
  category: 'etudes',
  title: 'S\'inscrire à l\'université en France',
  title_ar: 'التسجيل في الجامعة في فرنسا',
  description: 'Procédure via Parcoursup ou inscription directe, équivalences de diplômes tunisiens.',
  description_ar: 'الإجراءات عبر Parcoursup أو التسجيل المباشر ومعادلة الشهادات التونسية.',
  tags: ['Université', 'Parcoursup', 'Équivalence'],
  difficulty: 'Intermédiaire',
  isFree: true,
  isPopular: true,
  timeEstimate: '~15 min de lecture'
},
{
  id: 'prompt-etu-002',
  category: 'etudes',
  title: 'Bourses étudiantes disponibles',
  title_ar: 'المنح الدراسية المتاحة',
  description: 'Bourses sur critères sociaux, aides régionales et bourses spécifiques aux étudiants tunisiens.',
  description_ar: 'المنح الاجتماعية والمساعدات الإقليمية والمنح الخاصة بالطلاب التونسيين.',
  tags: ['Bourse', 'CROUS', 'Aide'],
  difficulty: 'Intermédiaire',
  isFree: false,
  isPopular: false,
  timeEstimate: '~12 min de lecture'
},
// Vie quotidienne
{
  id: 'prompt-quo-001',
  category: 'quotidien',
  title: 'Ouvrir un compte bancaire en France',
  title_ar: 'فتح حساب بنكي في فرنسا',
  description: 'Quelle banque choisir, documents nécessaires et droit au compte pour les étrangers.',
  description_ar: 'أي بنك تختار والوثائق المطلوبة وحق الحساب للأجانب.',
  tags: ['Banque', 'Compte', 'Droit au compte'],
  difficulty: 'Débutant',
  isFree: true,
  isPopular: true,
  timeEstimate: '~10 min de lecture'
},
{
  id: 'prompt-quo-002',
  category: 'quotidien',
  title: 'Déclaration d\'impôts pour les étrangers',
  title_ar: 'التصريح بالضرائب للأجانب',
  description: 'Quand et comment déclarer vos revenus en France, conventions fiscales franco-tunisiennes.',
  description_ar: 'متى وكيف تصرّح بدخلك في فرنسا والاتفاقيات الضريبية الفرنسية التونسية.',
  tags: ['Impôts', 'Déclaration', 'Convention'],
  difficulty: 'Intermédiaire',
  isFree: false,
  isPopular: false,
  timeEstimate: '~18 min de lecture'
},
{
  id: 'prompt-quo-003',
  category: 'quotidien',
  title: 'Convertir son permis de conduire tunisien',
  title_ar: 'تحويل رخصة القيادة التونسية',
  description: 'Échange du permis tunisien contre un permis français : conditions et démarches en préfecture.',
  description_ar: 'استبدال رخصة القيادة التونسية برخصة فرنسية: الشروط والإجراءات في المحافظة.',
  tags: ['Permis', 'Échange', 'Préfecture'],
  difficulty: 'Intermédiaire',
  isFree: true,
  isPopular: false,
  timeEstimate: '~12 min de lecture'
}];


// Guide content database - French
const guideContents: Record<string, GuideContent> = {
  'prompt-imm-001': {
    id: 'prompt-imm-001',
    title: 'Renouvellement du titre de séjour',
    timeEstimate: '~15 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['ANEF', 'Préfecture', '2–4 mois'],
    sections: [
    { type: 'intro', body: 'Le renouvellement de votre titre de séjour doit être demandé avant son expiration. En France, la procédure se fait principalement en ligne via le portail ANEF (Administration Numérique pour les Étrangers en France).' },
    { type: 'note', heading: 'Important', body: 'Déposez votre demande au moins 2 mois avant la date d\'expiration de votre titre actuel. Un récépissé vous sera remis, vous autorisant à rester en France pendant l\'instruction.' },
    { type: 'steps', heading: 'Étapes de la procédure', items: [
      'Connectez-vous sur le portail ANEF (administration-etrangers-en-france.interieur.gouv.fr) avec votre compte France Connect ou créez un compte.',
      'Sélectionnez "Renouveler mon titre de séjour" et choisissez le motif correspondant à votre situation.',
      'Rassemblez et numérisez tous les documents requis (voir liste ci-dessous).',
      'Remplissez le formulaire en ligne et téléchargez vos pièces justificatives.',
      'Payez les droits de timbre (environ 225 € selon le titre) par carte bancaire.',
      'Recevez votre récépissé par email — conservez-le précieusement.',
      'Attendez la convocation pour la remise du titre ou son envoi par courrier.']
    },
    { type: 'list', heading: 'Documents généralement requis', items: [
      'Titre de séjour actuel (recto-verso)',
      'Passeport en cours de validité (toutes les pages)',
      'Justificatif de domicile de moins de 3 mois',
      'Justificatifs de ressources (3 derniers bulletins de salaire ou avis d\'imposition)',
      'Photos d\'identité conformes aux normes françaises',
      'Justificatifs spécifiques selon le motif (contrat de travail, attestation de scolarité, etc.)']
    },
    { type: 'section', heading: 'Délais et suivi', body: 'Le délai d\'instruction est généralement de 2 à 4 mois. Vous pouvez suivre l\'avancement de votre dossier directement sur votre espace ANEF. En cas de demande de pièces complémentaires, vous serez notifié par email.' },
    { type: 'note', heading: 'Conseil', body: 'Si votre titre expire et que vous n\'avez pas encore reçu de réponse, votre récépissé fait office de titre de séjour provisoire. Renouvelez-le auprès de votre préfecture si nécessaire.' }]
  },
  'prompt-imm-002': {
    id: 'prompt-imm-002',
    title: 'Validation du visa long séjour (VLS-TS)',
    timeEstimate: '~10 min de lecture',
    difficulty: 'Débutant',
    tags: ['ANEF', 'Obligatoire', '3 mois'],
    sections: [
    { type: 'intro', body: 'Si vous êtes arrivé en France avec un visa long séjour valant titre de séjour (VLS-TS), vous devez obligatoirement le valider en ligne dans les 3 mois suivant votre arrivée. Cette démarche remplace l\'ancienne visite médicale à l\'OFII.' },
    { type: 'note', heading: 'Délai impératif', body: 'La validation doit être effectuée dans les 3 mois suivant votre entrée en France. Passé ce délai, votre visa perd sa valeur de titre de séjour.' },
    { type: 'steps', heading: 'Comment valider votre VLS-TS', items: [
      'Rendez-vous sur le portail ANEF (administration-etrangers-en-france.interieur.gouv.fr).',
      'Cliquez sur "Valider mon visa long séjour" dans la section dédiée.',
      'Créez un compte ou connectez-vous via France Connect.',
      'Renseignez les informations de votre visa et votre date d\'entrée en France.',
      'Téléchargez les documents demandés (passeport, justificatif d\'adresse).',
      'Payez la taxe de validation (200 € pour la plupart des titres).',
      'Recevez votre attestation de validation par email — imprimez-la et conservez-la avec votre passeport.']
    },
    { type: 'list', heading: 'Documents nécessaires', items: [
      'Passeport avec le visa VLS-TS',
      'Justificatif d\'adresse en France (quittance de loyer, attestation d\'hébergement...)',
      'Carte bancaire pour le paiement de la taxe']
    },
    { type: 'section', heading: 'Après la validation', body: 'Une fois validé, votre VLS-TS vous autorise à séjourner en France pour la durée indiquée sur votre visa. Avant son expiration, vous devrez demander un titre de séjour auprès de la préfecture ou via l\'ANEF.' }]
  },
  'prompt-trav-001': {
    id: 'prompt-trav-001',
    title: 'Comprendre sa fiche de paie',
    timeEstimate: '~12 min de lecture',
    difficulty: 'Débutant',
    tags: ['Salaire', 'Cotisations', 'Net/Brut'],
    sections: [
    { type: 'intro', body: 'La fiche de paie française peut sembler complexe au premier abord. Ce guide vous explique les éléments essentiels pour comprendre votre bulletin de salaire et vérifier qu\'il est correct.' },
    { type: 'section', heading: 'Salaire brut vs salaire net', body: 'Le salaire brut est le montant total avant déductions. Le salaire net est ce que vous recevez réellement sur votre compte. La différence correspond aux cotisations sociales (environ 22–25 % du brut pour un salarié).' },
    { type: 'list', heading: 'Principales cotisations salariales', items: [
      'Assurance maladie : couvre vos frais de santé via la Sécurité sociale',
      'Retraite de base et complémentaire : constitue vos droits à la retraite',
      'Assurance chômage : finance vos droits à l\'allocation chômage (ARE)',
      'CSG/CRDS : contributions sociales généralisées',
      'Mutuelle d\'entreprise : complémentaire santé (obligatoire depuis 2016)']
    },
    { type: 'section', heading: 'Congés payés', body: 'En France, vous acquérez 2,5 jours de congés payés par mois travaillé, soit 30 jours ouvrables (5 semaines) par an. Ils apparaissent sur votre fiche de paie sous forme de solde de congés.' },
    { type: 'list', heading: 'Éléments à vérifier sur votre bulletin', items: [
      'Votre nom, prénom et numéro de Sécurité sociale',
      'Le coefficient et la classification conventionnelle',
      'Le nombre d\'heures travaillées et les éventuelles heures supplémentaires',
      'Le montant du SMIC ou du salaire minimum conventionnel',
      'Les primes et indemnités (transport, repas, ancienneté)',
      'Le net imposable (base pour votre déclaration d\'impôts)']
    },
    { type: 'note', heading: 'Bon à savoir', body: 'Depuis 2019, le prélèvement à la source de l\'impôt sur le revenu apparaît directement sur votre fiche de paie. Le taux est calculé par l\'administration fiscale et peut être modifié sur impots.gouv.fr.' }]
  },
  'prompt-trav-002': {
    id: 'prompt-trav-002',
    title: 'S\'inscrire à France Travail (Pôle Emploi)',
    timeEstimate: '~10 min de lecture',
    difficulty: 'Débutant',
    tags: ['France Travail', 'ARE', 'Chômage'],
    sections: [
    { type: 'intro', body: 'France Travail (anciennement Pôle Emploi) est l\'organisme public chargé de l\'accompagnement des demandeurs d\'emploi et du versement des allocations chômage (ARE). En tant qu\'étranger en situation régulière, vous avez les mêmes droits que les ressortissants français.' },
    { type: 'steps', heading: 'Procédure d\'inscription', items: [
      'Rendez-vous sur francetravail.fr et cliquez sur "S\'inscrire comme demandeur d\'emploi".',
      'Créez votre espace personnel avec votre adresse email.',
      'Renseignez votre situation (fin de contrat, licenciement, démission...) et vos informations personnelles.',
      'Téléchargez les pièces justificatives demandées.',
      'Prenez rendez-vous avec un conseiller pour votre entretien d\'inscription.',
      'Recevez votre numéro de demandeur d\'emploi et accédez à votre espace personnel.']
    },
    { type: 'list', heading: 'Documents à préparer', items: [
      'Pièce d\'identité ou titre de séjour en cours de validité',
      'Attestation employeur (remise par votre dernier employeur)',
      'Relevé d\'identité bancaire (RIB)',
      'Curriculum vitae à jour',
      'Justificatif de domicile']
    },
    { type: 'section', heading: 'Conditions pour l\'ARE (allocation chômage)', body: 'Pour percevoir l\'allocation de retour à l\'emploi, vous devez avoir travaillé au moins 6 mois (130 jours ou 910 heures) au cours des 24 derniers mois, être involontairement privé d\'emploi, et être en recherche active d\'emploi.' },
    { type: 'note', heading: 'Pour les étrangers', body: 'Votre titre de séjour doit vous autoriser à travailler en France. Les droits à l\'ARE sont calculés sur la base de vos cotisations, indépendamment de votre nationalité. Pensez à signaler tout changement de situation (nouveau titre de séjour, départ à l\'étranger).' }]
  },
  'prompt-log-001': {
    id: 'prompt-log-001',
    title: 'Demander les APL à la CAF',
    timeEstimate: '~8 min de lecture',
    difficulty: 'Débutant',
    tags: ['CAF', 'APL', 'Simulation'],
    sections: [
    { type: 'intro', body: 'Les Aides Personnalisées au Logement (APL) sont des aides financières versées par la CAF (Caisse d\'Allocations Familiales) pour réduire le montant de votre loyer. Elles sont accessibles aux étrangers en situation régulière.' },
    { type: 'note', heading: 'Conditions d\'éligibilité', body: 'Vous devez être locataire d\'un logement conventionné, avoir un titre de séjour valide, et que le logement soit votre résidence principale. Les ressources du foyer sont prises en compte pour calculer le montant.' },
    { type: 'steps', heading: 'Comment faire votre demande', items: [
      'Rendez-vous sur caf.fr et créez votre compte personnel.',
      'Utilisez le simulateur en ligne pour estimer le montant de vos APL.',
      'Cliquez sur "Faire une demande d\'aide au logement" dans votre espace.',
      'Renseignez votre situation familiale, vos ressources et les informations sur votre logement.',
      'Téléchargez les pièces justificatives (bail, titre de séjour, RIB).',
      'Soumettez votre demande — la CAF vous contactera pour confirmer ou demander des compléments.']
    },
    { type: 'list', heading: 'Documents nécessaires', items: [
      'Contrat de bail signé',
      'Titre de séjour en cours de validité',
      'Avis d\'imposition ou déclaration de revenus',
      'RIB pour le versement',
      'Attestation de loyer (fournie par votre propriétaire)']
    },
    { type: 'section', heading: 'Montant et versement', body: 'Le montant des APL varie selon vos revenus, la composition de votre foyer, et le montant de votre loyer. Il est versé directement à votre propriétaire ou sur votre compte bancaire selon les cas. Pensez à actualiser votre situation chaque année.' }]
  },
  'prompt-log-002': {
    id: 'prompt-log-002',
    title: 'Garantie Visale pour votre location',
    timeEstimate: '~10 min de lecture',
    difficulty: 'Débutant',
    tags: ['Visale', 'Caution', 'Action Logement'],
    sections: [
    { type: 'intro', body: 'La garantie Visale est une caution gratuite proposée par Action Logement. Elle garantit le paiement de votre loyer au propriétaire en cas d\'impayé, ce qui facilite grandement l\'accès à la location pour les personnes sans garant.' },
    { type: 'list', heading: 'Qui peut en bénéficier ?', items: [
      'Salariés de moins de 30 ans (sans condition de type de contrat)',
      'Salariés de 30 ans et plus en CDI avec période d\'essai ou en CDD',
      'Personnes en situation précaire (sans emploi, bénéficiaires de minima sociaux)',
      'Étudiants et alternants sous certaines conditions']
    },
    { type: 'steps', heading: 'Démarches pour obtenir Visale', items: [
      'Rendez-vous sur visale.fr et créez votre compte.',
      'Vérifiez votre éligibilité en renseignant votre situation.',
      'Obtenez votre visa (attestation de garantie) en quelques minutes.',
      'Transmettez le visa à votre futur propriétaire lors de la signature du bail.',
      'Le propriétaire valide la garantie sur son espace Visale.',
      'En cas d\'impayé, Action Logement rembourse le propriétaire et vous propose un plan de remboursement.']
    },
    { type: 'note', heading: 'Avantage clé', body: 'La garantie Visale est totalement gratuite pour le locataire et le propriétaire. Elle couvre jusqu\'à 36 mois d\'impayés et les dégradations locatives. C\'est un argument fort pour convaincre un propriétaire hésitant.' }]
  },
  'prompt-san-001': {
    id: 'prompt-san-001',
    title: 'Obtenir sa carte Vitale',
    timeEstimate: '~8 min de lecture',
    difficulty: 'Débutant',
    tags: ['CPAM', 'Carte Vitale', 'Ameli'],
    sections: [
    { type: 'intro', body: 'La carte Vitale est la carte d\'assurance maladie française. Elle permet de bénéficier du remboursement de vos frais de santé par la Sécurité sociale. En tant que nouvel arrivant, vous devez d\'abord vous affilier à l\'Assurance Maladie.' },
    { type: 'steps', heading: 'Procédure d\'affiliation et demande de carte Vitale', items: [
      'Rendez-vous sur ameli.fr et créez votre compte Ameli.',
      'Téléchargez le formulaire de demande d\'affiliation (cerfa n°60-3766) ou faites la demande en ligne.',
      'Rassemblez les pièces justificatives (voir liste ci-dessous).',
      'Envoyez votre dossier à votre CPAM (Caisse Primaire d\'Assurance Maladie) locale.',
      'Recevez votre numéro de Sécurité sociale provisoire par courrier.',
      'Recevez votre carte Vitale définitive sous 2 à 4 semaines après validation.']
    },
    { type: 'list', heading: 'Documents à fournir', items: [
      'Titre de séjour en cours de validité',
      'Passeport',
      'Justificatif de domicile en France',
      'Relevé d\'identité bancaire (RIB)',
      'Justificatif de situation (contrat de travail, attestation de scolarité, etc.)',
      'Photo d\'identité récente']
    },
    { type: 'section', heading: 'En attendant votre carte Vitale', body: 'Pendant l\'instruction de votre dossier, vous pouvez obtenir une attestation de droits sur ameli.fr. Cette attestation peut être présentée chez le médecin ou à la pharmacie pour bénéficier du tiers payant.' },
    { type: 'note', heading: 'Mise à jour de la carte', body: 'Pensez à mettre à jour votre carte Vitale dans les bornes disponibles en pharmacie ou chez votre médecin après chaque changement de situation (naissance, mariage, changement d\'adresse).' }]
  },
  'prompt-san-002': {
    id: 'prompt-san-002',
    title: 'Choisir son médecin traitant',
    timeEstimate: '~6 min de lecture',
    difficulty: 'Débutant',
    tags: ['Médecin', 'Remboursement', 'Ameli'],
    sections: [
    { type: 'intro', body: 'En France, le parcours de soins coordonnés vous oblige à déclarer un médecin traitant pour bénéficier du meilleur taux de remboursement. Sans médecin traitant déclaré, vos remboursements seront réduits.' },
    { type: 'section', heading: 'Pourquoi déclarer un médecin traitant ?', body: 'Avec un médecin traitant déclaré, vos consultations sont remboursées à 70 % par l\'Assurance Maladie (contre 30 % sans). Votre mutuelle complète généralement le reste. Le médecin traitant coordonne votre parcours de soins et vous oriente vers des spécialistes si nécessaire.' },
    { type: 'steps', heading: 'Comment déclarer votre médecin traitant', items: [
      'Choisissez un médecin généraliste qui accepte de nouveaux patients (vérifiez sur doctolib.fr ou ameli.fr).',
      'Lors de votre première consultation, demandez au médecin de vous déclarer comme son patient.',
      'Le médecin remplit le formulaire de déclaration de médecin traitant.',
      'La déclaration est transmise automatiquement à votre CPAM.',
      'Vous recevez une confirmation sur votre espace ameli.fr.']
    },
    { type: 'note', heading: 'Difficulté à trouver un médecin', body: 'Dans certaines zones, il peut être difficile de trouver un médecin traitant disponible. Consultez le site ameli.fr rubrique "Trouver un médecin" ou contactez votre CPAM pour obtenir de l\'aide. Des maisons de santé pluriprofessionnelles peuvent aussi vous accueillir.' },
    { type: 'list', heading: 'Remboursements selon la situation', items: [
      'Consultation chez votre médecin traitant : remboursée à 70 %',
      'Spécialiste avec orientation du médecin traitant : remboursé à 70 %',
      'Spécialiste sans orientation (hors urgence) : remboursé à 30 %',
      'Urgences et médecins de garde : remboursés normalement sans pénalité']
    }]
  },
  'prompt-etu-001': {
    id: 'prompt-etu-001',
    title: 'S\'inscrire à l\'université en France',
    timeEstimate: '~15 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['Université', 'Parcoursup', 'Équivalence'],
    sections: [
    { type: 'intro', body: 'L\'inscription à l\'université française suit des procédures spécifiques selon votre niveau d\'études et votre situation. Pour les étudiants tunisiens, des démarches supplémentaires concernant l\'équivalence des diplômes peuvent être nécessaires.' },
    { type: 'section', heading: 'Parcoursup (Licence 1)', body: 'Pour intégrer une première année de licence, vous devez passer par la plateforme Parcoursup (parcoursup.fr). Les candidatures s\'ouvrent en janvier pour une rentrée en septembre. Les étudiants étrangers hors UE doivent souvent passer par la procédure "Études en France".' },
    { type: 'steps', heading: 'Procédure pour les étudiants tunisiens', items: [
      'Consultez le site Campus France Tunisie (campusfrance.org/tunisie) pour les démarches spécifiques.',
      'Créez un dossier sur la plateforme Études en France si vous postulez depuis la Tunisie.',
      'Passez les tests de langue française et les entretiens Campus France si requis.',
      'Obtenez votre visa étudiant auprès du consulat français en Tunisie.',
      'Après votre arrivée, finalisez votre inscription administrative à l\'université.',
      'Validez votre VLS-TS sur le portail ANEF dans les 3 mois.']
    },
    { type: 'list', heading: 'Équivalence des diplômes tunisiens', items: [
      'Le baccalauréat tunisien est généralement reconnu pour l\'entrée en Licence 1',
      'La Licence tunisienne (3 ans) est équivalente à la Licence française',
      'Le Master tunisien est reconnu pour l\'entrée en Master 2 ou Doctorat',
      'Pour certaines filières, une validation des acquis peut être demandée',
      'Contactez le CIEP (Centre International d\'Études Pédagogiques) pour une reconnaissance officielle']
    },
    { type: 'note', heading: 'Frais d\'inscription', body: 'Les frais d\'inscription à l\'université publique française sont réglementés. Pour les étudiants hors UE, ils peuvent être différenciés. Renseignez-vous auprès de l\'université visée et vérifiez votre éligibilité aux bourses du CROUS.' }]
  },
  'prompt-quo-001': {
    id: 'prompt-quo-001',
    title: 'Ouvrir un compte bancaire en France',
    timeEstimate: '~10 min de lecture',
    difficulty: 'Débutant',
    tags: ['Banque', 'Compte', 'Droit au compte'],
    sections: [
    { type: 'intro', body: 'Ouvrir un compte bancaire en France est une étape essentielle pour votre installation. Certaines banques peuvent refuser les non-résidents ou les personnes sans revenus stables, mais la loi française garantit un "droit au compte" pour tous.' },
    { type: 'list', heading: 'Types de banques disponibles', items: [
      'Banques traditionnelles (BNP Paribas, Société Générale, Crédit Agricole) : réseau d\'agences, services complets',
      'Banques en ligne (Boursorama, Hello Bank, Fortuneo) : frais réduits, tout en ligne',
      'Néobanques (N26, Revolut, Lydia) : ouverture rapide, idéal pour commencer',
      'La Banque Postale : accessible à tous, y compris sans revenus',
      'Banques mutualistes (Crédit Mutuel, Caisse d\'Épargne) : présence locale forte']
    },
    { type: 'list', heading: 'Documents généralement demandés', items: [
      'Pièce d\'identité ou titre de séjour valide',
      'Justificatif de domicile en France (moins de 3 mois)',
      'Justificatif de revenus ou de ressources',
      'Numéro de téléphone français',
      'Adresse email']
    },
    { type: 'section', heading: 'Le droit au compte', body: 'Si une banque refuse d\'ouvrir un compte, vous pouvez exercer votre "droit au compte". Contactez la Banque de France (banque-france.fr) qui désignera d\'office une banque pour vous ouvrir un compte avec les services bancaires de base.' },
    { type: 'note', heading: 'Conseil pour les nouveaux arrivants', body: 'Commencez par une néobanque (Revolut, N26) pour avoir rapidement un IBAN français, puis ouvrez un compte dans une banque traditionnelle pour les démarches administratives qui l\'exigent (CAF, employeur, etc.).' }]
  },
  'prompt-quo-003': {
    id: 'prompt-quo-003',
    title: 'Convertir son permis de conduire tunisien',
    timeEstimate: '~12 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['Permis', 'Échange', 'Préfecture'],
    sections: [
    { type: 'intro', body: 'La Tunisie et la France ont signé une convention d\'échange de permis de conduire. Cela signifie que vous pouvez échanger votre permis tunisien contre un permis français sans repasser l\'examen, sous certaines conditions.' },
    { type: 'note', heading: 'Condition principale', body: 'Vous devez avoir obtenu votre permis tunisien alors que vous étiez résident en Tunisie, et effectuer la demande d\'échange dans les 12 mois suivant votre installation en France.' },
    { type: 'steps', heading: 'Procédure d\'échange', items: [
      'Rassemblez les documents nécessaires (voir liste ci-dessous).',
      'Faites traduire votre permis tunisien par un traducteur assermenté.',
      'Déposez votre dossier en préfecture ou sous-préfecture de votre lieu de résidence.',
      'Recevez un récépissé vous autorisant à conduire pendant l\'instruction.',
      'Votre permis tunisien est retenu et envoyé en Tunisie.',
      'Recevez votre permis français par courrier (délai : 1 à 3 mois).']
    },
    { type: 'list', heading: 'Documents à fournir', items: [
      'Permis de conduire tunisien original',
      'Traduction officielle du permis par un traducteur assermenté',
      'Titre de séjour en cours de validité',
      'Justificatif de domicile en France',
      'Photo d\'identité aux normes françaises',
      'Formulaire CERFA n°14882*01 (demande d\'échange)']
    },
    { type: 'section', heading: 'Catégories échangeables', body: 'Les catégories B (voiture), A (moto) et autres catégories mentionnées sur votre permis tunisien peuvent être échangées. Vérifiez auprès de votre préfecture les catégories spécifiques couvertes par la convention franco-tunisienne.' }]
  },
  'prompt-imm-003': {
    id: 'prompt-imm-003',
    title: 'Changement de statut de séjour',
    timeEstimate: '~20 min de lecture',
    difficulty: 'Avancé',
    tags: ['Préfecture', 'ANEF', 'Dossier'],
    sections: [
    { type: 'intro', body: 'Le changement de statut consiste à modifier le motif de votre titre de séjour sans quitter la France. Par exemple, passer d\'un titre étudiant à un titre salarié, ou d\'un titre visiteur à un titre vie privée et familiale.' },
    { type: 'note', heading: 'Important', body: 'Le changement de statut n\'est pas un droit automatique. Il doit être justifié par un changement réel de situation et les conditions du nouveau titre doivent être remplies. Certains changements sont impossibles depuis la France.' },
    { type: 'steps', heading: 'Procédure générale', items: [
      'Vérifiez que votre changement de statut est possible depuis la France (consultez le site service-public.fr).',
      'Rassemblez les documents justifiant votre nouvelle situation (contrat de travail, acte de mariage, etc.).',
      'Déposez votre demande sur le portail ANEF avant l\'expiration de votre titre actuel.',
      'Payez les droits de timbre correspondant au nouveau titre.',
      'Recevez un récépissé de demande de changement de statut.',
      'Attendez la décision de la préfecture (délai : 2 à 6 mois selon les cas).',
      'En cas d\'accord, retirez votre nouveau titre de séjour ou recevez-le par courrier.']
    },
    { type: 'list', heading: 'Changements courants et conditions', items: [
      'Étudiant → Salarié : avoir un contrat de travail, l\'employeur doit obtenir une autorisation de travail',
      'Étudiant → Recherche d\'emploi/création d\'entreprise : avoir validé un Master ou équivalent',
      'Visiteur → Vie privée et familiale : justifier de liens familiaux en France',
      'Salarié → Entrepreneur : justifier d\'un projet viable et de ressources suffisantes']
    },
    { type: 'section', heading: 'Cas particulier : accord franco-tunisien', body: 'L\'accord bilatéral franco-tunisien prévoit des dispositions spécifiques pour certains changements de statut. Les ressortissants tunisiens peuvent bénéficier de conditions plus favorables pour certains titres. Renseignez-vous auprès de votre préfecture.' },
    { type: 'note', heading: 'Conseil', body: 'Consultez un juriste spécialisé en droit des étrangers ou une association d\'aide aux migrants (GISTI, France terre d\'asile) avant de déposer votre demande, surtout si votre situation est complexe.' }]
  },
  'prompt-imm-004': {
    id: 'prompt-imm-004',
    title: 'Demande de carte de résident 10 ans',
    timeEstimate: '~18 min de lecture',
    difficulty: 'Avancé',
    tags: ['Accord bilatéral', '10 ans', 'Stabilité'],
    sections: [
    { type: 'intro', body: 'La carte de résident est un titre de séjour valable 10 ans, renouvelable automatiquement. Elle offre une grande stabilité et permet d\'exercer toute activité professionnelle sans autorisation spécifique. L\'accord franco-tunisien prévoit des conditions particulières pour les ressortissants tunisiens.' },
    { type: 'list', heading: 'Conditions générales d\'obtention', items: [
      'Résider régulièrement en France depuis au moins 5 ans (certains cas : 3 ans)',
      'Justifier d\'une intégration républicaine (connaissance du français, respect des valeurs)',
      'Disposer de ressources stables et suffisantes',
      'Ne pas constituer une menace pour l\'ordre public',
      'Avoir souscrit au Contrat d\'Intégration Républicaine (CIR) si arrivé après 2007']
    },
    { type: 'section', heading: 'Dispositions de l\'accord franco-tunisien', body: 'L\'accord bilatéral du 17 mars 1988 modifié prévoit que certains ressortissants tunisiens peuvent obtenir une carte de résident après 3 ans de résidence régulière (au lieu de 5 ans), notamment les conjoints de Français et les parents d\'enfants français.' },
    { type: 'steps', heading: 'Procédure de demande', items: [
      'Vérifiez votre éligibilité selon votre situation et la durée de résidence.',
      'Rassemblez tous les documents justificatifs (voir liste).',
      'Déposez votre demande sur le portail ANEF ou en préfecture selon votre département.',
      'Payez les droits de timbre (225 €).',
      'Participez à l\'entretien d\'intégration si convoqué.',
      'Recevez la décision de la préfecture (délai : 3 à 6 mois).']
    },
    { type: 'list', heading: 'Documents à fournir', items: [
      'Titre de séjour actuel et tous les titres précédents',
      'Passeport en cours de validité',
      'Justificatifs de résidence régulière depuis 5 ans (ou 3 ans)',
      'Justificatifs de ressources stables',
      'Justificatif de logement',
      'Diplôme de langue française (si requis)',
      'Extrait de casier judiciaire']
    }]
  },
  'prompt-imm-005': {
    id: 'prompt-imm-005',
    title: 'Regroupement familial',
    timeEstimate: '~25 min de lecture',
    difficulty: 'Avancé',
    tags: ['OFII', 'Famille', 'Ressources'],
    sections: [
    { type: 'intro', body: 'Le regroupement familial permet à un étranger résidant légalement en France de faire venir son conjoint et ses enfants mineurs. La procédure est gérée par l\'OFII (Office Français de l\'Immigration et de l\'Intégration).' },
    { type: 'list', heading: 'Conditions pour le demandeur', items: [
      'Résider régulièrement en France depuis au moins 18 mois',
      'Disposer de ressources stables et suffisantes (au moins 1 SMIC net)',
      'Disposer d\'un logement adapté à la taille de la famille',
      'Ne pas être en situation de polygamie']
    },
    { type: 'steps', heading: 'Procédure de demande', items: [
      'Déposez votre demande auprès de l\'OFII ou de la préfecture de votre lieu de résidence.',
      'L\'OFII vérifie vos conditions de ressources et de logement.',
      'Les membres de la famille font une demande de visa long séjour au consulat français.',
      'Après obtention du visa, les membres de la famille arrivent en France.',
      'Ils doivent valider leur VLS-TS sur le portail ANEF dans les 3 mois.',
      'Ils bénéficient d\'un titre de séjour "vie privée et familiale".']
    },
    { type: 'note', heading: 'Délais', body: 'La procédure de regroupement familial peut prendre de 6 mois à 1 an. Préparez votre dossier avec soin et anticipez les délais.' }]
  },
  'prompt-imm-006': {
    id: 'prompt-imm-006',
    title: 'Demande de naturalisation française',
    timeEstimate: '~30 min de lecture',
    difficulty: 'Avancé',
    tags: ['Naturalisation', '5–7 ans', 'CIR'],
    sections: [
    { type: 'intro', body: 'La naturalisation permet d\'acquérir la nationalité française. Elle est accordée par décret après examen de votre dossier par le ministère de l\'Intérieur. La procédure est longue mais ouvre des droits importants.' },
    { type: 'list', heading: 'Conditions principales', items: [
      'Résider en France depuis au moins 5 ans de manière régulière et continue',
      'Avoir une connaissance suffisante du français (niveau B1 minimum)',
      'Justifier d\'une assimilation à la communauté française',
      'Être de bonne moralité (pas de condamnations pénales graves)',
      'Disposer de ressources suffisantes']
    },
    { type: 'steps', heading: 'Procédure de demande', items: [
      'Constituez votre dossier complet (liste sur service-public.fr).',
      'Déposez votre dossier en préfecture ou sous-préfecture.',
      'Passez un entretien d\'assimilation avec un agent de la préfecture.',
      'Votre dossier est transmis au ministère de l\'Intérieur pour décision.',
      'En cas d\'accord, vous recevez un décret de naturalisation.',
      'Participez à la cérémonie d\'accueil dans la citoyenneté française.']
    },
    { type: 'note', heading: 'Délais', body: 'La procédure de naturalisation prend généralement entre 12 et 24 mois. Soyez patient et conservez tous les justificatifs de votre intégration.' }]
  },
  'prompt-trav-003': {
    id: 'prompt-trav-003',
    title: 'Autorisation de travail pour étrangers',
    timeEstimate: '~15 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['Autorisation', 'Employeur', 'DIRECCTE'],
    sections: [
    { type: 'intro', body: 'En France, certains étrangers ont besoin d\'une autorisation de travail pour exercer une activité professionnelle. Cette autorisation est généralement demandée par l\'employeur auprès de la DREETS (ex-DIRECCTE).' },
    { type: 'list', heading: 'Qui a besoin d\'une autorisation ?', items: [
      'Les ressortissants hors UE/EEE/Suisse qui n\'ont pas de titre de séjour autorisant le travail',
      'Les titulaires d\'un visa étudiant souhaitant travailler plus de 964 heures par an',
      'Les personnes en changement de statut vers un titre salarié']
    },
    { type: 'steps', heading: 'Procédure pour l\'employeur', items: [
      'L\'employeur vérifie que le poste figure sur la liste des métiers en tension ou justifie la nécessité de recruter un étranger.',
      'Il dépose une demande d\'autorisation de travail auprès de la DREETS.',
      'La DREETS instruit le dossier et rend une décision sous 2 mois.',
      'En cas d\'accord, l\'employeur peut procéder à l\'embauche.',
      'Le salarié étranger peut alors demander un titre de séjour salarié.']
    },
    { type: 'note', heading: 'Accord franco-tunisien', body: 'L\'accord bilatéral franco-tunisien facilite l\'accès au marché du travail pour certains ressortissants tunisiens dans des secteurs spécifiques. Renseignez-vous auprès de la DREETS de votre région.' }]
  },
  'prompt-log-003': {
    id: 'prompt-log-003',
    title: 'Droits du locataire et état des lieux',
    timeEstimate: '~14 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['Bail', 'Locataire', 'Droits'],
    sections: [
    { type: 'intro', body: 'En France, le locataire bénéficie d\'une protection juridique importante. La loi encadre strictement les relations entre propriétaires et locataires, notamment en ce qui concerne le bail, l\'état des lieux et les charges.' },
    { type: 'list', heading: 'Vos droits essentiels', items: [
      'Droit à un logement décent et en bon état',
      'Protection contre les expulsions abusives',
      'Droit à la vie privée (le propriétaire ne peut entrer sans votre accord)',
      'Encadrement des loyers dans certaines villes',
      'Droit de sous-louer avec accord du propriétaire']
    },
    { type: 'steps', heading: 'L\'état des lieux', items: [
      'L\'état des lieux d\'entrée doit être réalisé le jour de la remise des clés.',
      'Notez tous les défauts existants, même mineurs, sur le document.',
      'Prenez des photos datées pour documenter l\'état du logement.',
      'Signez l\'état des lieux uniquement si vous êtes d\'accord avec son contenu.',
      'Conservez une copie de l\'état des lieux d\'entrée pendant toute la durée du bail.',
      'L\'état des lieux de sortie sera comparé à celui d\'entrée pour évaluer les dégradations.']
    },
    { type: 'note', heading: 'Dépôt de garantie', body: 'Le dépôt de garantie est limité à 1 mois de loyer hors charges pour un logement vide, 2 mois pour un meublé. Il doit vous être restitué dans les 2 mois suivant la restitution des clés, déduction faite des éventuelles réparations justifiées.' }]
  },
  'prompt-etu-002': {
    id: 'prompt-etu-002',
    title: 'Bourses étudiantes disponibles',
    timeEstimate: '~12 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['Bourse', 'CROUS', 'Aide'],
    sections: [
    { type: 'intro', body: 'Plusieurs types de bourses et d\'aides financières sont disponibles pour les étudiants en France. Les étudiants étrangers en situation régulière peuvent accéder à certaines de ces aides selon leur statut.' },
    { type: 'list', heading: 'Bourses sur critères sociaux (CROUS)', items: [
      'Accessibles aux étudiants inscrits dans un établissement d\'enseignement supérieur français',
      'Calculées en fonction des revenus des parents et de la situation familiale',
      'De 0 à 7 échelons selon les ressources (de 100 € à 600 € par mois environ)',
      'Les étudiants étrangers hors UE peuvent y accéder sous conditions']
    },
    { type: 'steps', heading: 'Comment faire une demande de bourse CROUS', items: [
      'Connectez-vous sur messervices.etudiant.gouv.fr.',
      'Remplissez le Dossier Social Étudiant (DSE) entre janvier et mai.',
      'Fournissez les justificatifs de revenus de vos parents.',
      'Recevez une notification conditionnelle de bourse.',
      'Finalisez votre inscription dans l\'établissement pour confirmer la bourse.']
    },
    { type: 'note', heading: 'Aides spécifiques aux Tunisiens', body: 'Des bourses du gouvernement tunisien peuvent être disponibles pour les étudiants tunisiens à l\'étranger. Renseignez-vous auprès du ministère de l\'Enseignement Supérieur tunisien et de l\'ambassade de Tunisie en France.' }]
  },
  'prompt-quo-002': {
    id: 'prompt-quo-002',
    title: 'Déclaration d\'impôts pour les étrangers',
    timeEstimate: '~18 min de lecture',
    difficulty: 'Intermédiaire',
    tags: ['Impôts', 'Déclaration', 'Convention'],
    sections: [
    { type: 'intro', body: 'Si vous résidez en France, vous êtes en principe soumis à l\'impôt sur le revenu français sur l\'ensemble de vos revenus mondiaux. La convention fiscale franco-tunisienne évite la double imposition.' },
    { type: 'list', heading: 'Qui doit déclarer ses revenus ?', items: [
      'Toute personne ayant son foyer fiscal en France',
      'Les personnes qui ont exercé une activité professionnelle en France',
      'Les personnes qui ont des revenus de source française',
      'Revenus d\'activité indépendante']
    },
    { type: 'steps', heading: 'Comment déclarer vos impôts', items: [
      'Rendez-vous sur impots.gouv.fr et créez votre espace particulier.',
      'Pour votre première déclaration, utilisez le formulaire 2042 (déclaration de revenus).',
      'Renseignez vos revenus de l\'année précédente (janvier à décembre).',
      'Déclarez également les revenus de source tunisienne si vous êtes résident fiscal français.',
      'Bénéficiez des déductions et crédits d\'impôt auxquels vous avez droit.',
      'Validez et soumettez votre déclaration avant la date limite (mai-juin selon votre département).']
    },
    { type: 'section', heading: 'Convention fiscale franco-tunisienne', body: 'La convention du 28 mai 1973 entre la France et la Tunisie évite la double imposition. Si vous avez payé des impôts en Tunisie sur des revenus de source tunisienne, vous bénéficiez d\'un crédit d\'impôt en France. Joignez les justificatifs de paiement d\'impôts tunisiens à votre déclaration.' },
    { type: 'note', heading: 'Prélèvement à la source', body: 'Depuis 2019, l\'impôt est prélevé directement sur votre salaire chaque mois. Le taux est calculé par l\'administration fiscale. Vous devez quand même déclarer vos revenus chaque année pour régulariser votre situation. Vous pouvez modifier votre taux sur impots.gouv.fr.' }]
  }
};

// Guide content database - Arabic
const guideContentsAr: Record<string, GuideContent> = {
  'prompt-imm-001': {
    id: 'prompt-imm-001',
    title: 'تجديد تصريح الإقامة',
    timeEstimate: '~15 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['ANEF', 'المحافظة', '2–4 أشهر'],
    sections: [
      { type: 'intro', body: 'يجب تقديم طلب تجديد تصريح الإقامة قبل انتهاء صلاحيته. في فرنسا، تتم الإجراءات بشكل رئيسي عبر الإنترنت من خلال بوابة ANEF (الإدارة الرقمية للأجانب في فرنسا).' },
      { type: 'note', heading: 'مهم', body: 'قدّم طلبك قبل شهرين على الأقل من تاريخ انتهاء تصريحك الحالي. ستحصل على وصل استلام يخوّلك البقاء في فرنسا أثناء معالجة الطلب.' },
      { type: 'steps', heading: 'خطوات الإجراء', items: [
        'سجّل الدخول إلى بوابة ANEF (administration-etrangers-en-france.interieur.gouv.fr) بحسابك على France Connect أو أنشئ حساباً جديداً.',
        'اختر "تجديد تصريح الإقامة" وحدد السبب المناسب لوضعك.',
        'اجمع وامسح ضوئياً جميع الوثائق المطلوبة (انظر القائمة أدناه).',
        'أكمل النموذج الإلكتروني وارفع مستنداتك الداعمة.',
        'ادفع رسوم الطوابع (حوالي 225 يورو حسب نوع التصريح) ببطاقة بنكية.',
        'استلم وصل الاستلام عبر البريد الإلكتروني — احتفظ به بعناية.',
        'انتظر الاستدعاء لاستلام التصريح أو إرساله بالبريد.']
      },
      { type: 'list', heading: 'الوثائق المطلوبة عموماً', items: [
        'تصريح الإقامة الحالي (الوجهان)',
        'جواز السفر ساري المفعول (جميع الصفحات)',
        'إثبات السكن لأقل من 3 أشهر',
        'إثبات الموارد (آخر 3 كشوف رواتب أو إشعار الضريبة)',
        'صور هوية مطابقة للمعايير الفرنسية',
        'وثائق خاصة حسب السبب (عقد عمل، شهادة دراسية، إلخ)']
      },
      { type: 'section', heading: 'المدد الزمنية والمتابعة', body: 'تستغرق معالجة الملف عادةً من 2 إلى 4 أشهر. يمكنك متابعة تقدم ملفك مباشرة على حسابك في ANEF. في حال طلب وثائق إضافية، ستُبلَّغ عبر البريد الإلكتروني.' },
      { type: 'note', heading: 'نصيحة', body: 'إذا انتهى تصريحك ولم تتلقَّ رداً بعد، فإن وصل الاستلام يعمل كتصريح إقامة مؤقت. جدّده في محافظتك إذا لزم الأمر.' }
    ]
  },
  'prompt-imm-002': {
    id: 'prompt-imm-002',
    title: 'التحقق من تأشيرة الإقامة الطويلة (VLS-TS)',
    timeEstimate: '~10 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['ANEF', 'إلزامي', '3 أشهر'],
    sections: [
      { type: 'intro', body: 'إذا وصلت إلى فرنسا بتأشيرة إقامة طويلة تُعادل تصريح إقامة (VLS-TS)، يجب عليك التحقق منها إلزامياً عبر الإنترنت خلال 3 أشهر من وصولك. هذا الإجراء يحل محل الزيارة الطبية القديمة لدى OFII.' },
      { type: 'note', heading: 'موعد نهائي إلزامي', body: 'يجب إتمام التحقق خلال 3 أشهر من دخولك إلى فرنسا. بعد هذا الأجل، تفقد تأشيرتك قيمتها كتصريح إقامة.' },
      { type: 'steps', heading: 'كيفية التحقق من VLS-TS', items: [
        'توجّه إلى بوابة ANEF (administration-etrangers-en-france.interieur.gouv.fr).',
        'انقر على "التحقق من تأشيرة الإقامة الطويلة" في القسم المخصص.',
        'أنشئ حساباً أو سجّل الدخول عبر France Connect.',
        'أدخل معلومات تأشيرتك وتاريخ دخولك إلى فرنسا.',
        'ارفع الوثائق المطلوبة (جواز السفر، إثبات العنوان).',
        'ادفع رسوم التحقق (200 يورو لمعظم التصاريح).',
        'استلم شهادة التحقق عبر البريد الإلكتروني — اطبعها واحتفظ بها مع جواز سفرك.']
      },
      { type: 'list', heading: 'الوثائق اللازمة', items: [
        'جواز السفر مع تأشيرة VLS-TS',
        'إثبات العنوان في فرنسا (إيصال الإيجار، شهادة الإيواء...)',
        'بطاقة بنكية لدفع الرسوم']
      },
      { type: 'section', heading: 'بعد التحقق', body: 'بعد التحقق، تخوّلك VLS-TS الإقامة في فرنسا للمدة المحددة في تأشيرتك. قبل انتهائها، يجب عليك طلب تصريح إقامة من المحافظة أو عبر ANEF.' }
    ]
  },
  'prompt-trav-001': {
    id: 'prompt-trav-001',
    title: 'فهم كشف الراتب',
    timeEstimate: '~12 دقيقة قراءة',
    difficulty: 'Débutant',
    tags: ['الراتب', 'الاشتراكات', 'الإجمالي/الصافي'],
    sections: [
      { type: 'intro', body: 'قد يبدو كشف الراتب الفرنسي معقداً للوهلة الأولى. يشرح لك هذا الدليل العناصر الأساسية لفهم بيان راتبك والتحقق من صحته.' },
      { type: 'section', heading: 'الراتب الإجمالي مقابل الراتب الصافي', body: 'الراتب الإجمالي هو المبلغ الكلي قبل الخصومات. الراتب الصافي هو ما تتلقاه فعلياً في حسابك. الفرق يمثل الاشتراكات الاجتماعية (حوالي 22–25٪ من الإجمالي للموظف).' },
      { type: 'list', heading: 'الاشتراكات الرئيسية للموظف', items: [
        'التأمين الصحي: يغطي نفقاتك الصحية عبر الضمان الاجتماعي',
        'التقاعد الأساسي والتكميلي: يُكوّن حقوقك في التقاعد',
        'تأمين البطالة: يموّل حقوقك في تعويض البطالة (ARE)',
        'CSG/CRDS: مساهمات اجتماعية عامة',
        'التأمين الصحي التكميلي للشركة: إلزامي منذ 2016']
      },
      { type: 'section', heading: 'الإجازات المدفوعة', body: 'في فرنسا، تكتسب 2.5 يوم إجازة مدفوعة لكل شهر عمل، أي 30 يوماً عملياً (5 أسابيع) في السنة. تظهر في كشف راتبك كرصيد إجازات.' },
      { type: 'list', heading: 'عناصر يجب التحقق منها في كشف راتبك', items: [
        'اسمك ولقبك ورقم الضمان الاجتماعي',
        'المعامل والتصنيف الاتفاقي',
        'عدد ساعات العمل وأي ساعات إضافية',
        'مبلغ الحد الأدنى للأجور (SMIC) أو الحد الأدنى الاتفاقي',
        'العلاوات والتعويضات (النقل، الوجبات، الأقدمية)',
        'الصافي الخاضع للضريبة (أساس تصريحك الضريبي)']
      },
      { type: 'note', heading: 'معلومة مفيدة', body: 'منذ 2019، يُقتطع ضريبة الدخل مباشرة من راتبك كل شهر. يُحسب المعدل من قِبل الإدارة الضريبية ويمكن تعديله على impots.gouv.fr.' }
    ]
  },
  'prompt-trav-002': {
    id: 'prompt-trav-002',
    title: 'التسجيل في France Travail (بول إمبلوا)',
    timeEstimate: '~10 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['France Travail', 'ARE', 'البطالة'],
    sections: [
      { type: 'intro', body: 'France Travail (المعروف سابقاً بـ Pôle Emploi) هو الهيئة العامة المسؤولة عن مرافقة طالبي العمل وصرف تعويضات البطالة (ARE). بوصفك أجنبياً في وضع قانوني، تتمتع بنفس الحقوق التي يتمتع بها المواطنون الفرنسيون.' },
      { type: 'steps', heading: 'إجراءات التسجيل', items: [
        'توجّه إلى francetravail.fr وانقر على "التسجيل كطالب عمل".',
        'أنشئ حسابك الشخصي بعنوان بريدك الإلكتروني.',
        'أدخل وضعك (انتهاء العقد، الفصل، الاستقالة...) ومعلوماتك الشخصية.',
        'ارفع الوثائق الداعمة المطلوبة.',
        'احجز موعداً مع مستشار لمقابلة التسجيل.',
        'استلم رقم طالب العمل وادخل إلى حسابك الشخصي.']
      },
      { type: 'list', heading: 'الوثائق الواجب تحضيرها', items: [
        'وثيقة هوية أو تصريح إقامة ساري المفعول',
        'شهادة صاحب العمل (يسلّمها آخر صاحب عمل)',
        'كشف الهوية البنكية (RIB)',
        'سيرة ذاتية محدّثة',
        'إثبات السكن']
      },
      { type: 'section', heading: 'شروط الحصول على ARE (تعويض البطالة)', body: 'للحصول على تعويض العودة إلى العمل، يجب أن تكون قد عملت 6 أشهر على الأقل (130 يوماً أو 910 ساعات) خلال الـ24 شهراً الماضية، وأن تكون محروماً من العمل بشكل لا إرادي، وأن تكون في بحث فعلي عن عمل.' },
      { type: 'note', heading: 'للأجانب', body: 'يجب أن يخوّلك تصريح إقامتك العمل في فرنسا. تُحسب حقوق ARE على أساس اشتراكاتك بصرف النظر عن جنسيتك. تذكّر الإبلاغ عن أي تغيير في وضعك (تصريح إقامة جديد، سفر إلى الخارج).' }
    ]
  },
  'prompt-log-001': {
    id: 'prompt-log-001',
    title: 'طلب مساعدات السكن APL من CAF',
    timeEstimate: '~8 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['CAF', 'APL', 'محاكاة'],
    sections: [
      { type: 'intro', body: 'مساعدات السكن الشخصية (APL) هي مساعدات مالية تصرفها CAF (صندوق التخصيصات العائلية) لتخفيض مبلغ إيجارك. يمكن للأجانب في وضع قانوني الاستفادة منها.' },
      { type: 'note', heading: 'شروط الأهلية', body: 'يجب أن تكون مستأجراً لسكن متعاقد عليه، وأن يكون لديك تصريح إقامة ساري، وأن يكون السكن مقر إقامتك الرئيسي. تُؤخذ موارد الأسرة بعين الاعتبار لحساب المبلغ.' },
      { type: 'steps', heading: 'كيفية تقديم طلبك', items: [
        'توجّه إلى caf.fr وأنشئ حسابك الشخصي.',
        'استخدم المحاكي الإلكتروني لتقدير مبلغ APL.',
        'انقر على "تقديم طلب مساعدة السكن" في حسابك.',
        'أدخل وضعك العائلي وموارد وبيانات سكنك.',
        'ارفع الوثائق الداعمة (عقد الإيجار، تصريح الإقامة، RIB).',
        'أرسل طلبك — ستتصل بك CAF للتأكيد أو طلب مستندات إضافية.']
      },
      { type: 'list', heading: 'الوثائق اللازمة', items: [
        'عقد الإيجار الموقّع',
        'تصريح الإقامة ساري المفعول',
        'إشعار الضريبة أو تصريح الدخل',
        'كشف الهوية البنكية (RIB) للصرف',
        'شهادة الإيجار (يوفرها مالك العقار)']
      },
      { type: 'section', heading: 'المبلغ والصرف', body: 'يتفاوت مبلغ APL حسب دخلك وتركيبة أسرتك ومبلغ إيجارك. يُصرف مباشرة لمالك العقار أو في حسابك البنكي حسب الحالة. تذكّر تحديث وضعك كل سنة.' }
    ]
  },
  'prompt-log-002': {
    id: 'prompt-log-002',
    title: 'ضمان Visale لإيجارك',
    timeEstimate: '~10 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['Visale', 'كفالة', 'Action Logement'],
    sections: [
      { type: 'intro', body: 'ضمان Visale هو كفالة مجانية تقدمها Action Logement. يضمن دفع إيجارك للمالك في حال التأخر، مما يسهّل كثيراً الحصول على سكن للأشخاص الذين لا يملكون كفيلاً.' },
      { type: 'list', heading: 'من يمكنه الاستفادة؟', items: [
        'الموظفون دون 30 سنة (بغض النظر عن نوع العقد)',
        'الموظفون من 30 سنة فأكثر بعقد CDI في فترة التجربة أو بعقد CDD',
        'الأشخاص في وضع هش (بدون عمل، مستفيدون من الحد الأدنى الاجتماعي)',
        'الطلاب والمتدربون في ظروف معينة']
      },
      { type: 'steps', heading: 'إجراءات الحصول على Visale', items: [
        'توجّه إلى visale.fr وأنشئ حسابك.',
        'تحقق من أهليتك بإدخال وضعك.',
        'احصل على تأشيرتك (شهادة الضمان) في دقائق.',
        'سلّم التأشيرة لمالك العقار المستقبلي عند توقيع عقد الإيجار.',
        'يُصادق المالك على الضمان في حسابه على Visale.',
        'في حال التأخر، تُعوّض Action Logement المالك وتقترح عليك خطة سداد.']
      },
      { type: 'note', heading: 'الميزة الرئيسية', body: 'ضمان Visale مجاني تماماً للمستأجر والمالك. يغطي حتى 36 شهراً من التأخر في الدفع والأضرار المادية. إنه حجة قوية لإقناع مالك متردد.' }
    ]
  },
  'prompt-san-001': {
    id: 'prompt-san-001',
    title: 'الحصول على بطاقة Vitale',
    timeEstimate: '~8 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['CPAM', 'بطاقة Vitale', 'Ameli'],
    sections: [
      { type: 'intro', body: 'بطاقة Vitale هي بطاقة التأمين الصحي الفرنسية. تتيح لك الاستفادة من تعويض نفقاتك الصحية من الضمان الاجتماعي. بوصفك قادماً جديداً، يجب عليك أولاً الانتساب إلى التأمين الصحي.' },
      { type: 'steps', heading: 'إجراءات الانتساب وطلب بطاقة Vitale', items: [
        'توجّه إلى ameli.fr وأنشئ حسابك على Ameli.',
        'نزّل نموذج طلب الانتساب (cerfa n°60-3766) أو قدّم الطلب إلكترونياً.',
        'اجمع الوثائق الداعمة (انظر القائمة أدناه).',
        'أرسل ملفك إلى CPAM (الصندوق الأولي للتأمين الصحي) المحلي.',
        'استلم رقم الضمان الاجتماعي المؤقت بالبريد.',
        'استلم بطاقة Vitale النهائية خلال 2 إلى 4 أسابيع بعد التحقق.']
      },
      { type: 'list', heading: 'الوثائق الواجب تقديمها', items: [
        'تصريح الإقامة ساري المفعول',
        'جواز السفر',
        'إثبات السكن في فرنسا',
        'كشف الهوية البنكية (RIB)',
        'إثبات الوضع (عقد عمل، شهادة دراسية، إلخ)',
        'صورة هوية حديثة']
      },
      { type: 'section', heading: 'في انتظار بطاقة Vitale', body: 'أثناء معالجة ملفك، يمكنك الحصول على شهادة الحقوق على ameli.fr. يمكن تقديم هذه الشهادة عند الطبيب أو الصيدلية للاستفادة من الدفع المباشر.' },
      { type: 'note', heading: 'تحديث البطاقة', body: 'تذكّر تحديث بطاقة Vitale في الأجهزة المتاحة في الصيدليات أو عند طبيبك بعد كل تغيير في وضعك (ولادة، زواج، تغيير العنوان).' }
    ]
  },
  'prompt-san-002': {
    id: 'prompt-san-002',
    title: 'اختيار طبيبك المعالج',
    timeEstimate: '~6 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['الطبيب', 'التعويض', 'Ameli'],
    sections: [
      { type: 'intro', body: 'في فرنسا، يُلزمك مسار الرعاية الصحية المنسّق بالإعلان عن طبيب معالج للاستفادة من أفضل نسبة تعويض. بدون طبيب معالج معلَن، ستنخفض تعويضاتك.' },
      { type: 'section', heading: 'لماذا تُعلن عن طبيب معالج؟', body: 'مع طبيب معالج معلَن، تُعوَّض استشاراتك بنسبة 70٪ من التأمين الصحي (مقابل 30٪ بدونه). تُكمّل التأمينة التكميلية عادةً الباقي. يُنسّق الطبيب المعالج مسار رعايتك ويُحيلك إلى المختصين عند الحاجة.' },
      { type: 'steps', heading: 'كيفية الإعلان عن طبيبك المعالج', items: [
        'اختر طبيباً عاماً يقبل مرضى جدداً (تحقق على doctolib.fr أو ameli.fr).',
        'خلال استشارتك الأولى، اطلب من الطبيب تسجيلك كمريض لديه.',
        'يملأ الطبيب نموذج الإعلان عن الطبيب المعالج.',
        'يُرسَل الإعلان تلقائياً إلى CPAM.',
        'تتلقى تأكيداً على حسابك في ameli.fr.']
      },
      { type: 'note', heading: 'صعوبة إيجاد طبيب', body: 'في بعض المناطق، قد يكون من الصعب إيجاد طبيب معالج متاح. راجع ameli.fr في قسم "إيجاد طبيب" أو اتصل بـ CPAM للحصول على مساعدة. يمكن أن تستقبلك أيضاً مراكز الصحة متعددة التخصصات.' },
      { type: 'list', heading: 'التعويضات حسب الوضع', items: [
        'الاستشارة عند طبيبك المعالج: تُعوَّض بنسبة 70٪',
        'المختص بإحالة من الطبيب المعالج: يُعوَّض بنسبة 70٪',
        'المختص بدون إحالة (خارج الطوارئ): يُعوَّض بنسبة 30٪',
        'الطوارئ وأطباء المناوبة: يُعوَّضون بشكل طبيعي بدون عقوبة']
      }
    ]
  },
  'prompt-etu-001': {
    id: 'prompt-etu-001',
    title: 'التسجيل في الجامعة في فرنسا',
    timeEstimate: '~15 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['الجامعة', 'Parcoursup', 'معادلة الشهادات'],
    sections: [
      { type: 'intro', body: 'يتبع التسجيل في الجامعة الفرنسية إجراءات محددة حسب مستواك الدراسي ووضعك. بالنسبة للطلاب التونسيين، قد تكون هناك خطوات إضافية تتعلق بمعادلة الشهادات.' },
      { type: 'section', heading: 'Parcoursup (السنة الأولى ليسانس)', body: 'للالتحاق بالسنة الأولى من الليسانس، يجب المرور عبر منصة Parcoursup (parcoursup.fr). تفتح الترشيحات في يناير لدخول سبتمبر. غالباً ما يجب على الطلاب الأجانب من خارج الاتحاد الأوروبي المرور بإجراء "الدراسة في فرنسا".' },
      { type: 'steps', heading: 'الإجراءات للطلاب التونسيين', items: [
        'راجع موقع Campus France تونس (campusfrance.org/tunisie) للإجراءات الخاصة.',
        'أنشئ ملفاً على منصة "الدراسة في فرنسا" إذا كنت تتقدم من تونس.',
        'أجرِ اختبارات اللغة الفرنسية ومقابلات Campus France إذا طُلب ذلك.',
        'احصل على تأشيرة الطالب من القنصلية الفرنسية في تونس.',
        'بعد وصولك، أتمّ تسجيلك الإداري في الجامعة.',
        'تحقق من VLS-TS على بوابة ANEF خلال 3 أشهر.']
      },
      { type: 'list', heading: 'معادلة الشهادات التونسية', items: [
        'البكالوريا التونسية معترف بها عموماً للالتحاق بالسنة الأولى ليسانس',
        'الليسانس التونسية (3 سنوات) تعادل الليسانس الفرنسية',
        'الماجستير التوني معترف به للالتحاق بالماجستير 2 أو الدكتوراه',
        'لبعض التخصصات، قد يُطلب التحقق من المكتسبات',
        'تواصل مع CIEP (المركز الدولي للدراسات التربوية) للاعتراف الرسمي']
      },
      { type: 'note', heading: 'رسوم التسجيل', body: 'رسوم التسجيل في الجامعة العامة الفرنسية محددة بقانون. بالنسبة للطلاب من خارج الاتحاد الأوروبي، قد تكون مختلفة. استفسر من الجامعة المستهدفة وتحقق من أهليتك للمنح من CROUS.' }
    ]
  },
  'prompt-quo-001': {
    id: 'prompt-quo-001',
    title: 'فتح حساب بنكي في فرنسا',
    timeEstimate: '~10 دقائق قراءة',
    difficulty: 'Débutant',
    tags: ['البنك', 'الحساب', 'حق الحساب'],
    sections: [
      { type: 'intro', body: 'فتح حساب بنكي في فرنسا خطوة أساسية لاستقرارك. قد ترفض بعض البنوك غير المقيمين أو الأشخاص بدون دخل ثابت، لكن القانون الفرنسي يضمن "حق الحساب" للجميع.' },
      { type: 'list', heading: 'أنواع البنوك المتاحة', items: [
        'البنوك التقليدية (BNP Paribas، Société Générale، Crédit Agricole): شبكة فروع، خدمات متكاملة',
        'البنوك الإلكترونية (Boursorama، Hello Bank، Fortuneo): رسوم منخفضة، كل شيء عبر الإنترنت',
        'البنوك الرقمية (N26، Revolut، Lydia): فتح سريع، مثالي للبداية',
        'La Banque Postale: متاحة للجميع بما فيهم من لا دخل لهم',
        'البنوك التعاونية (Crédit Mutuel، Caisse d\'Épargne): حضور محلي قوي']
      },
      { type: 'list', heading: 'الوثائق المطلوبة عموماً', items: [
        'وثيقة هوية أو تصريح إقامة ساري',
        'إثبات السكن في فرنسا (أقل من 3 أشهر)',
        'إثبات الدخل أو الموارد',
        'رقم هاتف فرنسي',
        'عنوان بريد إلكتروني']
      },
      { type: 'section', heading: 'حق الحساب', body: 'إذا رفض بنك فتح حساب لك، يمكنك ممارسة "حق الحساب". تواصل مع Banque de France (banque-france.fr) التي ستُعيّن بنكاً بحكم القانون لفتح حساب لك بالخدمات البنكية الأساسية.' },
      { type: 'note', heading: 'نصيحة للقادمين الجدد', body: 'ابدأ ببنك رقمي (Revolut، N26) للحصول بسرعة على IBAN فرنسي، ثم افتح حساباً في بنك تقليدي للإجراءات الإدارية التي تستلزمه (CAF، صاحب العمل، إلخ).' }
    ]
  },
  'prompt-quo-003': {
    id: 'prompt-quo-003',
    title: 'تحويل رخصة القيادة التونسية',
    timeEstimate: '~12 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['رخصة القيادة', 'الاستبدال', 'المحافظة'],
    sections: [
      { type: 'intro', body: 'وقّعت تونس وفرنسا اتفاقية لاستبدال رخص القيادة. هذا يعني أنه يمكنك استبدال رخصتك التونسية برخصة فرنسية دون إعادة الامتحان، وفق شروط معينة.' },
      { type: 'note', heading: 'الشرط الرئيسي', body: 'يجب أن تكون قد حصلت على رخصتك التونسية وأنت مقيم في تونس، وأن تقدّم طلب الاستبدال خلال 12 شهراً من استقرارك في فرنسا.' },
      { type: 'steps', heading: 'إجراءات الاستبدال', items: [
        'اجمع الوثائق اللازمة (انظر القائمة أدناه).',
        'اطلب ترجمة رخصتك التونسية من مترجم محلَّف.',
        'قدّم ملفك في المحافظة أو المحافظة الفرعية لمكان إقامتك.',
        'استلم وصلاً يخوّلك القيادة أثناء معالجة الطلب.',
        'تُحتجز رخصتك التونسية وتُرسل إلى تونس.',
        'استلم رخصتك الفرنسية بالبريد (مدة: 1 إلى 3 أشهر).']
      },
      { type: 'list', heading: 'الوثائق الواجب تقديمها', items: [
        'رخصة القيادة التونسية الأصلية',
        'ترجمة رسمية للرخصة من مترجم محلَّف',
        'تصريح الإقامة ساري المفعول',
        'إثبات السكن في فرنسا',
        'صورة هوية مطابقة للمعايير الفرنسية',
        'نموذج CERFA n°14882*01 (طلب الاستبدال)']
      },
      { type: 'section', heading: 'الفئات القابلة للاستبدال', body: 'يمكن استبدال الفئة B (السيارة) والفئة A (الدراجة النارية) وغيرها من الفئات المذكورة في رخصتك التونسية. تحقق من المحافظة بشأن الفئات المحددة التي تشملها الاتفاقية الفرنسية التونسية.' }
    ]
  },
  'prompt-imm-003': {
    id: 'prompt-imm-003',
    title: 'تغيير وضع الإقامة',
    timeEstimate: '~20 دقيقة قراءة',
    difficulty: 'Avancé',
    tags: ['المحافظة', 'ANEF', 'الملف'],
    sections: [
      { type: 'intro', body: 'يتمثل تغيير الوضع في تعديل سبب تصريح إقامتك دون مغادرة فرنسا. مثلاً، الانتقال من تصريح طالب إلى تصريح موظف، أو من تصريح زائر إلى تصريح حياة خاصة وعائلية.' },
      { type: 'note', heading: 'مهم', body: 'تغيير الوضع ليس حقاً تلقائياً. يجب تبريره بتغيير حقيقي في الوضع ويجب استيفاء شروط التصريح الجديد. بعض التغييرات مستحيلة من داخل فرنسا.' },
      { type: 'steps', heading: 'الإجراء العام', items: [
        'تحقق من إمكانية تغيير وضعك من داخل فرنسا (راجع service-public.fr).',
        'اجمع الوثائق المبررة لوضعك الجديد (عقد عمل، عقد زواج، إلخ).',
        'قدّم طلبك على بوابة ANEF قبل انتهاء تصريحك الحالي.',
        'ادفع رسوم الطوابع المقابلة للتصريح الجديد.',
        'استلم وصل طلب تغيير الوضع.',
        'انتظر قرار المحافظة (مدة: 2 إلى 6 أشهر حسب الحالة).',
        'في حال الموافقة، استلم تصريح إقامتك الجديد أو احصل عليه بالبريد.']
      },
      { type: 'list', heading: 'التغييرات الشائعة وشروطها', items: [
        'طالب ← موظف: امتلاك عقد عمل، يجب على صاحب العمل الحصول على تصريح عمل',
        'طالب ← باحث عن عمل/إنشاء مشروع: إتمام الماجستير أو ما يعادله',
        'زائر ← حياة خاصة وعائلية: إثبات روابط عائلية في فرنسا',
        'موظف ← رائد أعمال: إثبات مشروع قابل للتطبيق وموارد كافية']
      },
      { type: 'note', heading: 'نصيحة', body: 'استشر محامياً متخصصاً في قانون الأجانب أو جمعية مساعدة المهاجرين (GISTI، France terre d\'asile) قبل تقديم طلبك، خاصة إذا كان وضعك معقداً.' }
    ]
  },
  'prompt-imm-004': {
    id: 'prompt-imm-004',
    title: 'طلب بطاقة إقامة لمدة 10 سنوات',
    timeEstimate: '~18 دقيقة قراءة',
    difficulty: 'Avancé',
    tags: ['اتفاقية ثنائية', '10 سنوات', 'الاستقرار'],
    sections: [
      { type: 'intro', body: 'بطاقة الإقامة هي تصريح إقامة صالح لمدة 10 سنوات قابل للتجديد تلقائياً. توفر استقراراً كبيراً وتتيح ممارسة أي نشاط مهني دون تصريح خاص. تنص الاتفاقية الفرنسية التونسية على شروط خاصة للمواطنين التونسيين.' },
      { type: 'list', heading: 'الشروط العامة للحصول عليها', items: [
        'الإقامة بصفة قانونية في فرنسا منذ 5 سنوات على الأقل (بعض الحالات: 3 سنوات)',
        'إثبات الاندماج الجمهوري (معرفة الفرنسية، احترام القيم)',
        'امتلاك موارد مستقرة وكافية',
        'عدم تشكيل تهديد للنظام العام',
        'الانخراط في عقد الاندماج الجمهوري (CIR) إذا وصلت بعد 2007']
      },
      { type: 'section', heading: 'أحكام الاتفاقية الفرنسية التونسية', body: 'تنص الاتفاقية الثنائية المؤرخة في 17 مارس 1988 المعدّلة على أن بعض المواطنين التونسيين يمكنهم الحصول على بطاقة إقامة بعد 3 سنوات من الإقامة القانونية (بدلاً من 5 سنوات)، ولا سيما أزواج الفرنسيين وآباء الأطفال الفرنسيين.' },
      { type: 'steps', heading: 'إجراءات الطلب', items: [
        'تحقق من أهليتك حسب وضعك ومدة الإقامة.',
        'اجمع جميع الوثائق الداعمة (انظر القائمة).',
        'قدّم طلبك على بوابة ANEF أو في المحافظة حسب قسمك.',
        'ادفع رسوم الطوابع (225 يورو).',
        'شارك في مقابلة الاندماج إذا استُدعيت.',
        'استلم قرار المحافظة (مدة: 3 إلى 6 أشهر).']
      },
      { type: 'list', heading: 'الوثائق الواجب تقديمها', items: [
        'تصريح الإقامة الحالي وجميع التصاريح السابقة',
        'جواز السفر ساري المفعول',
        'إثبات الإقامة القانونية منذ 5 سنوات (أو 3 سنوات)',
        'إثبات الموارد المستقرة',
        'إثبات السكن',
        'شهادة اللغة الفرنسية (إذا طُلبت)',
        'مستخرج من السجل العدلي']
      }
    ]
  },
  'prompt-imm-005': {
    id: 'prompt-imm-005',
    title: 'لمّ شمل الأسرة',
    timeEstimate: '~25 دقيقة قراءة',
    difficulty: 'Avancé',
    tags: ['OFII', 'الأسرة', 'الموارد'],
    sections: [
      { type: 'intro', body: 'يتيح لمّ شمل الأسرة للأجنبي المقيم قانونياً في فرنسا إحضار زوجه وأطفاله القاصرين. تُدير هذه الإجراءات OFII (المكتب الفرنسي للهجرة والاندماج).' },
      { type: 'list', heading: 'شروط المتقدم', items: [
        'الإقامة بصفة قانونية في فرنسا منذ 18 شهراً على الأقل',
        'امتلاك موارد مستقرة وكافية (ما لا يقل عن الحد الأدنى للأجور صافياً)',
        'امتلاك سكن مناسب لحجم الأسرة',
        'عدم الوقوع في حالة تعدد الزوجات']
      },
      { type: 'steps', heading: 'إجراءات الطلب', items: [
        'قدّم طلبك لدى OFII أو محافظة مكان إقامتك.',
        'تتحقق OFII من شروط مواردك وسكنك.',
        'يتقدم أفراد الأسرة بطلب تأشيرة إقامة طويلة في القنصلية الفرنسية.',
        'بعد الحصول على التأشيرة، يصل أفراد الأسرة إلى فرنسا.',
        'يجب عليهم التحقق من VLS-TS على بوابة ANEF خلال 3 أشهر.',
        'يحصلون على تصريح إقامة "حياة خاصة وعائلية".']
      },
      { type: 'note', heading: 'المدد الزمنية', body: 'قد تستغرق إجراءات لمّ شمل الأسرة من 6 أشهر إلى سنة. حضّر ملفك بعناية وتوقّع المدد الزمنية.' }
    ]
  },
  'prompt-imm-006': {
    id: 'prompt-imm-006',
    title: 'طلب الجنسية الفرنسية',
    timeEstimate: '~30 دقيقة قراءة',
    difficulty: 'Avancé',
    tags: ['التجنيس', '5–7 سنوات', 'CIR'],
    sections: [
      { type: 'intro', body: 'يتيح التجنيس اكتساب الجنسية الفرنسية. يُمنح بمرسوم بعد فحص ملفك من وزارة الداخلية. الإجراء طويل لكنه يفتح حقوقاً مهمة.' },
      { type: 'list', heading: 'الشروط الرئيسية', items: [
        'الإقامة في فرنسا منذ 5 سنوات على الأقل بصفة قانونية ومستمرة',
        'امتلاك معرفة كافية باللغة الفرنسية (مستوى B1 كحد أدنى)',
        'إثبات الاندماج في المجتمع الفرنسي',
        'حسن السيرة والسلوك (لا إدانات جنائية خطيرة)',
        'امتلاك موارد كافية']
      },
      { type: 'steps', heading: 'إجراءات الطلب', items: [
        'أعدّ ملفك الكامل (القائمة على service-public.fr).',
        'قدّم ملفك في المحافظة أو المحافظة الفرعية.',
        'أجرِ مقابلة الاندماج مع موظف المحافظة.',
        'يُحال ملفك إلى وزارة الداخلية للبت فيه.',
        'في حال الموافقة، تتلقى مرسوم التجنيس.',
        'شارك في حفل الاستقبال في المواطنة الفرنسية.']
      },
      { type: 'note', heading: 'المدد الزمنية', body: 'تستغرق إجراءات التجنيس عادةً من 12 إلى 24 شهراً. كن صبوراً واحتفظ بجميع إثباتات اندماجك.' }
    ]
  },
  'prompt-trav-003': {
    id: 'prompt-trav-003',
    title: 'تصريح العمل للأجانب',
    timeEstimate: '~15 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['التصريح', 'صاحب العمل', 'DREETS'],
    sections: [
      { type: 'intro', body: 'في فرنسا، يحتاج بعض الأجانب إلى تصريح عمل لممارسة نشاط مهني. يُطلب هذا التصريح عادةً من صاحب العمل لدى DREETS (المديرية الإقليمية للاقتصاد والعمل).' },
      { type: 'list', heading: 'من يحتاج إلى تصريح؟', items: [
        'المواطنون من خارج الاتحاد الأوروبي/المنطقة الاقتصادية الأوروبية/سويسرا الذين لا يملكون تصريح إقامة يخوّل العمل',
        'حاملو تأشيرة الطالب الراغبون في العمل أكثر من 964 ساعة في السنة',
        'الأشخاص في تغيير الوضع نحو تصريح موظف']
      },
      { type: 'steps', heading: 'الإجراءات لصاحب العمل', items: [
        'يتحقق صاحب العمل من أن المنصب مدرج في قائمة المهن الشاغرة أو يبرر ضرورة توظيف أجنبي.',
        'يقدّم طلب تصريح عمل لدى DREETS.',
        'تدرس DREETS الملف وتصدر قراراً خلال شهرين.',
        'في حال الموافقة، يمكن لصاحب العمل المضي في التوظيف.',
        'يمكن للموظف الأجنبي حينئذٍ طلب تصريح إقامة موظف.']
      },
      { type: 'note', heading: 'الاتفاقية الفرنسية التونسية', body: 'تُيسّر الاتفاقية الثنائية الفرنسية التونسية الوصول إلى سوق العمل لبعض المواطنين التونسيين في قطاعات محددة. استفسر من DREETS في منطقتك.' }
    ]
  },
  'prompt-log-003': {
    id: 'prompt-log-003',
    title: 'حقوق المستأجر وجرد الممتلكات',
    timeEstimate: '~14 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['عقد الإيجار', 'المستأجر', 'الحقوق'],
    sections: [
      { type: 'intro', body: 'في فرنسا، يتمتع المستأجر بحماية قانونية مهمة. ينظّم القانون بصرامة العلاقات بين الملاك والمستأجرين، ولا سيما فيما يتعلق بعقد الإيجار وجرد الممتلكات والأعباء.' },
      { type: 'list', heading: 'حقوقك الأساسية', items: [
        'الحق في سكن لائق وفي حالة جيدة',
        'الحماية من الإخلاء التعسفي',
        'الحق في الخصوصية (لا يمكن للمالك الدخول بدون موافقتك)',
        'تنظيم الإيجارات في بعض المدن',
        'الحق في الإيجار من الباطن بموافقة المالك']
      },
      { type: 'steps', heading: 'جرد الممتلكات', items: [
        'يجب إجراء جرد الدخول يوم تسليم المفاتيح.',
        'سجّل جميع العيوب الموجودة، حتى البسيطة منها، في الوثيقة.',
        'التقط صوراً مؤرخة لتوثيق حالة السكن.',
        'وقّع جرد الممتلكات فقط إذا كنت موافقاً على محتواه.',
        'احتفظ بنسخة من جرد الدخول طوال مدة عقد الإيجار.',
        'سيُقارن جرد الخروج بجرد الدخول لتقييم الأضرار.']
      },
      { type: 'note', heading: 'وديعة الضمان', body: 'تقتصر وديعة الضمان على شهر إيجار بدون أعباء للسكن الفارغ، وشهرين للسكن المفروش. يجب إعادتها إليك خلال شهرين من تسليم المفاتيح، مطروحاً منها أي إصلاحات مبررة.' }
    ]
  },
  'prompt-etu-002': {
    id: 'prompt-etu-002',
    title: 'المنح الدراسية المتاحة',
    timeEstimate: '~12 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['المنحة', 'CROUS', 'المساعدة'],
    sections: [
      { type: 'intro', body: 'تتوفر أنواع عديدة من المنح والمساعدات المالية للطلاب في فرنسا. يمكن للطلاب الأجانب في وضع قانوني الاستفادة من بعض هذه المساعدات حسب وضعهم.' },
      { type: 'list', heading: 'المنح الاجتماعية (CROUS)', items: [
        'متاحة للطلاب المسجلين في مؤسسة تعليم عالٍ فرنسية',
        'تُحسب بناءً على دخل الوالدين والوضع العائلي',
        'من 0 إلى 7 درجات حسب الموارد (من 100 إلى 600 يورو شهرياً تقريباً)',
        'يمكن للطلاب الأجانب من خارج الاتحاد الأوروبي الاستفادة منها وفق شروط']
      },
      { type: 'steps', heading: 'كيفية تقديم طلب منحة CROUS', items: [
        'سجّل الدخول إلى messervices.etudiant.gouv.fr.',
        'أكمل الملف الاجتماعي للطالب (DSE) بين يناير ومايو.',
        'قدّم إثباتات دخل والديك.',
        'استلم إشعاراً مشروطاً بالمنحة.',
        'أتمّ تسجيلك في المؤسسة لتأكيد المنحة.']
      },
      { type: 'note', heading: 'مساعدات خاصة بالتونسيين', body: 'قد تتوفر منح من الحكومة التونسية للطلاب التونسيين في الخارج. استفسر من وزارة التعليم العالي التونسية وسفارة تونس في فرنسا.' }
    ]
  },
  'prompt-quo-002': {
    id: 'prompt-quo-002',
    title: 'التصريح بالضرائب للأجانب',
    timeEstimate: '~18 دقيقة قراءة',
    difficulty: 'Intermédiaire',
    tags: ['الضرائب', 'التصريح', 'الاتفاقية'],
    sections: [
      { type: 'intro', body: 'إذا كنت مقيماً في فرنسا، فأنت مبدئياً خاضع لضريبة الدخل الفرنسية على مجموع دخلك العالمي. تمنع الاتفاقية الضريبية الفرنسية التونسية الازدواج الضريبي.' },
      { type: 'list', heading: 'من يجب أن يُصرّح بدخله؟', items: [
        'كل شخص مقره الضريبي في فرنسا',
        'الأشخاص الذين مارسوا نشاطاً مهنياً في فرنسا',
        'الأشخاص الذين لديهم دخل من مصدر فرنسي',
        'دخل النشاط المستقل']
      },
      { type: 'steps', heading: 'كيفية التصريح بضرائبك', items: [
        'توجّه إلى impots.gouv.fr وأنشئ حسابك الشخصي.',
        'لتصريحك الأول، استخدم النموذج 2042 (تصريح الدخل).',
        'أدخل دخلك للسنة الماضية (يناير إلى ديسمبر).',
        'صرّح أيضاً بالدخل من المصدر التونسي إذا كنت مقيماً ضريبياً فرنسياً.',
        'استفد من الخصومات والتخفيضات الضريبية التي تستحقها.',
        'تحقق وأرسل تصريحك قبل الموعد النهائي (مايو-يونيو حسب قسمك).']
      },
      { type: 'section', heading: 'الاتفاقية الضريبية الفرنسية التونسية', body: 'تمنع اتفاقية 28 مايو 1973 بين فرنسا وتونس الازدواج الضريبي. إذا دفعت ضرائب في تونس على دخل من مصدر تونسي، تستفيد من تخفيض ضريبي في فرنسا. أرفق إثباتات دفع الضرائب التونسية بتصريحك.' },
      { type: 'note', heading: 'الاقتطاع من المصدر', body: 'منذ 2019، تُقتطع الضريبة مباشرة من راتبك كل شهر. يُحسب المعدل من قِبل الإدارة الضريبية. يجب عليك مع ذلك التصريح بدخلك كل سنة لتسوية وضعك. يمكنك تعديل معدلك على impots.gouv.fr.' }
    ]
  }
};

export default function PromptsGrid() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedGuide, setSelectedGuide] = useState<GuideContent | null>(null);
  const { t, isRTL, language } = useLanguage();

  const categories = categoryIds.map((c) => ({
    ...c,
    label: t.prompts.categories[c.id as keyof typeof t.prompts.categories] || c.id,
  }));

  const difficultyConfig: Record<string, {label: string;color: string;}> = {
    Débutant: { label: t.prompts.difficulty['Débutant'], color: 'text-green-600 bg-green-50' },
    Intermédiaire: { label: t.prompts.difficulty['Intermédiaire'], color: 'text-amber-600 bg-amber-50' },
    Avancé: { label: t.prompts.difficulty['Avancé'], color: 'text-red-600 bg-red-50' },
  };

  const filtered = activeCategory === 'all' ?
  prompts :
  prompts.filter((p) => p.category === activeCategory);

  const handleOpenGuide = (promptId: string) => {
    const contentMap = language === 'ar' ? guideContentsAr : guideContents;
    const content = contentMap[promptId];
    if (content) {
      setSelectedGuide(content);
    }
  };

  const sectionTitle = language === 'ar' ? 'أدلة سريعة' : 'GUIDES RAPIDES';
  const sectionHeading = language === 'ar' ? 'ابحث عن إجراءاتك' : 'Trouvez votre démarche';
  const sectionDesc = language === 'ar' ?'تصفّح أدلتنا الإدارية المعدّة من المصادر الرسمية الفرنسية. الوصول إلى الأدلة مجاني — سجّل الدخول لطرح أسئلتك على المساعد.' :'Parcourez nos guides administratifs rédigés à partir des sources officielles françaises. L\'accès aux guides est libre — connectez-vous pour poser vos questions à l\'assistant.';
  const guidesAvailable = language === 'ar' ? 'دليل متاح' : 'guides disponibles';
  const noGuideTitle = language === 'ar' ? 'لا يوجد دليل في هذه الفئة' : 'Aucun guide dans cette catégorie';
  const noGuideDesc = language === 'ar' ?'هذا القسم سيكون متاحاً قريباً. في انتظار ذلك، تصفّح الفئات الأخرى أو اطرح سؤالك على المساعد.' :'Cette section sera bientôt disponible. En attendant, consultez les autres catégories ou posez votre question à l\'assistant.';
  const seeAllGuides = language === 'ar' ? 'عرض جميع الأدلة' : 'Voir tous les guides';

  return (
    <>
      <section id="prompts" className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-12 md:py-16" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-medium text-accent-600 mb-1.5 font-jakarta tracking-wide uppercase">{sectionTitle}</p>
            <h2 className="font-jakarta text-2xl md:text-3xl font-700 text-gray-900">
              {sectionHeading}
            </h2>
            <p className="mt-2 text-gray-500 text-sm max-w-xl">
              {sectionDesc}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="tabular-nums font-medium text-primary-700">{filtered.length}</span>
            <span>{guidesAvailable}</span>
          </div>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => {
            const CatFilterIcon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={`cat-filter-${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive ?
                'bg-primary-700 text-white shadow-sm' :
                'bg-white text-gray-600 border border-gray-200 hover:border-primary-200 hover:text-primary-700 hover:bg-primary-50'}`
                }>

                <CatFilterIcon size={14} className={isActive ? 'text-white' : cat.color} />
                {cat.label}
              </button>);

          })}
        </div>

        {/* Prompts grid */}
        {filtered.length === 0 ?
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <FileText size={28} className="text-gray-400" />
            </div>
            <h3 className="font-jakarta text-lg font-600 text-gray-700 mb-2">
              {noGuideTitle}
            </h3>
            <p className="text-gray-400 text-sm max-w-sm">
              {noGuideDesc}
            </p>
            <button
            onClick={() => setActiveCategory('all')}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors">

              {seeAllGuides}
            </button>
          </div> :

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
            {filtered.map((prompt) => {
            const catConfig = categories.find((c) => c.id === prompt.category);
            const CatIcon = catConfig?.icon || FileText;
            const diff = difficultyConfig[prompt.difficulty];
            const displayTitle = language === 'ar' ? prompt.title_ar : prompt.title;
            const displayDesc = language === 'ar' ? prompt.description_ar : prompt.description;
            const hasContent = !!(language === 'ar' ? guideContentsAr[prompt.id] : guideContents[prompt.id]);

            return (
              <div
                key={prompt.id}
                className="group relative bg-white rounded-2xl border border-gray-100 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 flex flex-col overflow-hidden">

                  {/* Popular badge */}
                  {prompt.isPopular &&
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-xs font-medium text-amber-600 z-10">
                      <Star size={10} className="fill-amber-400 text-amber-400" />
                      {t.prompts.popular}
                    </div>
                }

                  <div className="p-5 flex-1 flex flex-col gap-3">
                    {/* Category icon */}
                    <div className={`w-10 h-10 rounded-xl ${catConfig?.bg || 'bg-gray-100'} flex items-center justify-center`}>
                      <CatIcon size={18} className={catConfig?.color || 'text-gray-600'} />
                    </div>

                    {/* Title & description */}
                    <div className="flex-1">
                      <h3 className="font-jakarta text-[15px] font-600 text-gray-900 leading-snug mb-1.5 group-hover:text-primary-700 transition-colors">
                        {displayTitle}
                      </h3>
                      <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                        {displayDesc}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5">
                      {prompt.tags.slice(0, 3).map((tag) =>
                    <span
                      key={`${prompt.id}-tag-${tag}`}
                      className="px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-500">

                          {tag}
                        </span>
                    )}
                    </div>

                    {/* Meta row */}
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${diff?.color}`}>
                          {diff?.label}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock size={11} />
                          {prompt.timeEstimate}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CTA footer */}
                  <div className="px-5 pb-4">
                    <button
                    onClick={() => handleOpenGuide(prompt.id)}
                    disabled={!hasContent}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 transition-all duration-150 group-hover:bg-primary-700 group-hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">

                      {t.prompts.openGuide}
                      <ArrowRight size={14} className={isRTL ? 'rotate-180' : ''} />
                    </button>
                  </div>
                </div>);

          })}
          </div>
        }
      </section>

      {/* Guide Modal */}
      <GuideModal guide={selectedGuide} onClose={() => setSelectedGuide(null)} />
    </>);

}